import React from 'react';
import { makeStyles } from 'react-scale/components/design-system/styles';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { Button } from 'design-system';
import { MentionsInput, Mention, SuggestionDataItem } from 'react-mentions';
import ReactTooltip from 'react-tooltip';
import ExperimentTaskInfoModal from 'react-scale/components/scalers/tasks/common/ExperimentTaskInfoModal';
import { FEEDBACK_MIN_CHARS } from '@scale/shared/selfServe/calibrationScore';
import _ from 'lodash';

/* Autocomplete textarea */

interface AutocompleteProps {
  data: SuggestionDataItem[];
  value?: string;
  onChange?: any;
  onAdd?: any;
  isScaleStudio?: boolean;
}

const AutocompleteTextArea: React.FC<AutocompleteProps> = props => {
  const inputEl = React.createRef<HTMLTextAreaElement>();

  const placeholder = props.isScaleStudio
    ? `Add feedback about the task instructions. Was anything unclear? What could have made it better?\n\nYou can use '@' to mention specific labels`
    : `Please take the time to read the instructions carefully and leave detailed feedback. We want to hear from you how the instructions can be improved. Avoid phrases such as "Instructions were clear" or "Instructions were well-written" as that is not very helpful. Be sure to leave feedback about the instructions, not any work already performed on the task.

Please don't rush! If your feedback is poor, you will get less Experiment Tasks in the future and you miss out on the bonus pay!

You can use '@' to mention specific labels
    `;

  return (
    <>
      <div style={{ height: '200px' }}>
        <MentionsInput
          style={{
            height: '200px',

            suggestions: {
              list: {
                backgroundColor: 'white',
                border: '1px solid rgba(0,0,0,0.15)',
                fontSize: 14,
              },
              item: {
                padding: '0px 12px',
                borderBottom: '1px solid rgba(0,0,0,0.15)',
                '&focused': {
                  backgroundColor: '#cee4e5',
                },
              },
            },
          }}
          value={props.value}
          onChange={props.onChange}
          placeholder={placeholder}
          inputRef={inputEl}
        >
          <Mention
            appendSpaceOnAdd
            trigger="@"
            displayTransform={(login: $FIXME) => `@${login}`}
            data={props.data}
            onAdd={props.onAdd}
            markup={'@[__display__]'}
          />
        </MentionsInput>
      </div>
      <samp></samp>
    </>
  );
};

/* Main instruction feedback form */

interface InstructionFeedbackFormProps {
  taskTimer?: any;
  // display props
  subtask: string;
  onClose: () => void;
  setSubmit: () => void;
  labels: string[];
  originalText: string;

  // props to edit text
  text: string;
  onChange: any;
  onAdd: any;

  // props to edit confidence
  isConfident: boolean | undefined;
  onSetConfidence: any;

  // props to edit arr
  taggedLabels: string[];

  // lidar toggle for style
  lidar?: boolean;

  // make some modifications if studio project
  isScaleStudio?: boolean;

  feedbackRequired?: boolean;
  readOnly?: boolean;
}

const TRIGGER_WORDS = [
  'clear',
  'concise',
  'well-written',
  'pay',
  'payement',
  'payment',
  'easy',
  'tasker',
  'claim',
];

const checkTaskerWarning = (text: string, originalText: string, taskTimer: any, rand: number) => {
  if (rand > 0.9) {
    return confirm(
      `Are you sure the task is done correctly? We've detected a potential issue with your work. Please make sure the task is labeled correctly. Submitting incomplete work will cause you to miss out on tasks that pay 30-50% bonus. If you prefer not to work on this task, please use the "Opt out" button.`,
    );
  }

  if (originalText === text && rand > 0.25) {
    return confirm(
      `Are you sure the feedback is good quality? You haven't edited the feedback. If feedback is poor you will miss out on 30-50% bonus pay.`,
    );
  }
  const tokens = text.toLowerCase().split(' ');
  if (_.some(tokens, t => TRIGGER_WORDS.includes(t)) && rand > 0.1) {
    return confirm(
      `Are you sure you've added helpful feedback about the instructions? You won't get full credit if the feedback is not helpful.`,
    );
  }

  // same message, different randomness threshold base on timespent
  if (taskTimer) {
    const msg = `Are you sure the task is complete? Please complete the task before writing your feedback. If anything is unclear, try your best and then add feedback to justify why you labeled the task this way. Otherwise, you will not get full credit.`;
    if (taskTimer.elapsedTime() / 1000.0 < 60 && rand > 0.5) {
      return confirm(msg);
    } else if (taskTimer.elapsedTime() / 1000.0 < 120 && rand > 0.65) {
      return confirm(msg);
    } else if (taskTimer.elapsedTime() / 1000.0 < 300 && rand > 0.8) {
      return confirm(msg);
    }
  }

  return true;
};

const InstructionFeedbackForm: React.FC<InstructionFeedbackFormProps> = ({
  taskTimer,
  subtask,
  onClose,
  setSubmit,
  labels,
  lidar,
  onChange,
  onAdd,
  text,
  originalText,
  onSetConfidence,
  isConfident,
  taggedLabels,
  isScaleStudio,
  feedbackRequired = false,
  readOnly = false,
}) => {
  const classes = useStyles();

  const rand = Math.random();

  const onSubmit = async () => {
    if (!isScaleStudio && !checkTaskerWarning(text, originalText, taskTimer, rand)) return;

    // 0.5 for undefined is maintained for backwards compatibility
    // previously submitted feedback doesn't have a confidence assessment

    // in case labels were deleted, check for label in set inside feedback string
    const filteredTaggedLabels: string[] = taggedLabels.filter(label => text.includes(label));

    const feedback: Record<string, any> = {
      text,
      confidence: (isConfident === undefined ? 0.5 : isConfident ? 1 : 0).toString(),
      taggedLabels: filteredTaggedLabels,
    };

    const body = {
      subtask,
      feedback: JSON.stringify(feedback),
    };
    await apiFetch(null, `internal/self_serve/instruction_feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setSubmit();
    onClose();
  };

  const labelArray = labels.map(label => {
    return { id: label, display: label };
  });

  const lengthWarning =
    feedbackRequired && text.trim().length < FEEDBACK_MIN_CHARS && !(isScaleStudio && isConfident)
      ? 'Please write more about your thinking process'
      : undefined;

  const renderForm = () => {
    const isSelected = isConfident !== undefined;
    return (
      <div>
        <div>
          {!isScaleStudio && (
            <div className={classes.section}>
              <div className={classes.sectionTitle}>
                <ExperimentTaskInfoModal subtask={subtask} />
              </div>
            </div>
          )}
          <div className={classes.section}>
            <div className={classes.sectionTitle}>Instruction Feedback</div>
            <AutocompleteTextArea
              data={labelArray}
              value={text}
              onChange={readOnly ? () => {} : onChange}
              onAdd={onAdd}
              isScaleStudio={isScaleStudio}
            />
          </div>
          <div className={classes.section}>
            <div
              className={classes.sectionTitle}
            >{`How confident did you feel completing this task?`}</div>
            <div
              className={classes.buttonInnerWrapper}
              style={{
                border: isSelected && isConfident ? '1px solid #2B99D8' : '1px solid #E5E7EB',
              }}
            >
              <Button
                onClick={() => !readOnly && onSetConfidence(true)}
                style={{ backgroundColor: 'white' }}
              >
                I confidently completed the task
              </Button>
            </div>
            <div
              className={classes.buttonInnerWrapper}
              style={{
                border: isSelected && !isConfident ? '1px solid #2B99D8' : '1px solid #E5E7EB',
              }}
            >
              <Button
                onClick={() => !readOnly && onSetConfidence(false)}
                style={{ backgroundColor: 'white' }}
              >
                I have concerns about the task
              </Button>
            </div>
            {!readOnly && (
              <div
                data-tip
                data-for="submit-disabled"
                style={{
                  marginTop: 8,
                  display: 'inline',
                }}
              >
                <Button
                  onClick={() => onSubmit()}
                  disabled={!isSelected || lengthWarning !== undefined}
                >
                  Submit
                </Button>
              </div>
            )}
            {lengthWarning && (
              <ReactTooltip id="submit-disabled" type="dark">
                <div style={{ fontWeight: 'bold' }}>{lengthWarning}</div>
              </ReactTooltip>
            )}
            <Button
              onClick={() => {
                onClose();
              }}
              disabled={false}
              style={{ marginLeft: 4 }}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (!lidar) {
    return <div className={classes.instructionFeedbackForm}>{renderForm()}</div>;
  } else {
    return <div className={classes.instructionFeedbackFormLidar}>{renderForm()}</div>;
  }
};

const useStyles = makeStyles({
  instructionFeedbackForm: {
    padding: '16px',
    alignItems: 'center',
    backgroundColor: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    position: 'absolute',
    bottom: '0',
    right: '0',
    zIndex: 9,
    width: 'min(100%, 600px)',
  },
  instructionFeedbackFormLidar: {
    padding: '16px',
    alignItems: 'center',
    backgroundColor: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    position: 'absolute',
    width: 'min(100%, 600px)',
    top: '0',
    zIndex: 9,
  },
  sectionTitle: {
    fontWeight: 'bold',
    padding: '4px',
  },
  section: {
    padding: '4px',
  },
  buttonInnerWrapper: {
    width: '-webkit-fill-available',
    boxSizing: 'border-box',
    backgroundColor: 'white',
    padding: '4px',
    marginTop: '8px',
    marginBottom: '8px',
    borderRadius: '15px',
  },
});

export default InstructionFeedbackForm;
